import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Helmet } from 'react-helmet-async';
import NewsletterSignUp from '../../common/Newsletter-sign';
import { getCanonicalUrl } from '../../../utils/seo';

const NAV_OFFSET = '158px';
const GO_LIVE_DATE = new Date('2026-06-19T09:00:00');

const SAFARI = {
  jungle: '#0C2C1B',
  forest: '#1B5E3B',
  safari: '#C8861A',
  cream: '#FBF4E8',
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  position: fixed;
  inset: 0;
  padding-top: ${NAV_OFFSET};
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  background: ${SAFARI.jungle};
`;

const Hero = styled.section`
  position: relative;
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(0.5rem, 2vh, 1.25rem) clamp(1rem, 3vw, 2rem);
  overflow: hidden;
  color: #fff;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      repeating-linear-gradient(
        60deg,
        transparent,
        transparent 38px,
        rgba(200, 134, 26, 0.04) 38px,
        rgba(200, 134, 26, 0.04) 40px
      ),
      repeating-linear-gradient(
        -60deg,
        transparent,
        transparent 38px,
        rgba(27, 94, 59, 0.08) 38px,
        rgba(27, 94, 59, 0.08) 40px
      );
    pointer-events: none;
  }
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  gap: clamp(1rem, 3vw, 2.5rem);
  align-items: center;
  width: min(100%, 68rem);
  max-height: 100%;
  animation: ${fadeUp} 0.6s ease;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: clamp(0.75rem, 2vh, 1.25rem);
    text-align: center;
  }
`;

const CopyColumn = styled.div`
  min-width: 0;
`;

const Kicker = styled.p`
  margin: 0 0 clamp(0.35rem, 1vh, 0.6rem);
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.62rem, 1.4vh, 0.72rem);
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${SAFARI.safari};
`;

const Title = styled.h1`
  margin: 0 0 clamp(0.35rem, 1vh, 0.65rem);
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(2rem, 6.5vh, 4.25rem);
  font-weight: 700;
  line-height: 1.02;
  color: #fff;

  em {
    font-style: italic;
    color: ${SAFARI.safari};
  }
`;

const Strapline = styled.p`
  margin: 0 0 clamp(0.65rem, 1.5vh, 1rem);
  max-width: 28rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(0.95rem, 2.2vh, 1.35rem);
  font-style: italic;
  font-weight: 300;
  line-height: 1.35;
  color: rgba(255, 255, 255, 0.72);

  @media (max-width: 860px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const CountdownSection = styled.div``;

const CountdownLabel = styled.p`
  margin: 0 0 clamp(0.5rem, 1.2vh, 0.85rem);
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.62rem, 1.3vh, 0.7rem);
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.45);
`;

const CountdownGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: clamp(0.45rem, 1.2vw, 0.75rem);

  @media (max-width: 860px) {
    justify-content: center;
  }
`;

const CountdownUnit = styled.div`
  min-width: clamp(58px, 11vw, 76px);
  padding: clamp(0.55rem, 1.4vh, 0.85rem) clamp(0.5rem, 1.2vw, 0.75rem);
  border-radius: 8px;
  border: 1px solid rgba(200, 134, 26, 0.35);
  background: rgba(12, 44, 27, 0.55);
  backdrop-filter: blur(8px);
`;

const CountdownNumber = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.45rem, 3.8vh, 2.2rem);
  font-weight: 700;
  line-height: 1;
  color: #fff;
  margin-bottom: 0.2rem;
`;

const CountdownName = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.58rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
`;

const LiveMessage = styled.p`
  margin: 0;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.25rem, 3.5vh, 1.85rem);
  font-weight: 600;
  color: ${SAFARI.safari};
`;

const SignupCard = styled.div`
  min-width: 0;
  padding: clamp(1rem, 2.5vh, 1.5rem);
  border-radius: 12px;
  border: 1px solid rgba(200, 134, 26, 0.25);
  background: ${SAFARI.cream};
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.24);
  text-align: left;
`;

const SignupKicker = styled.p`
  margin: 0 0 0.35rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${SAFARI.forest};
`;

const SignupTitle = styled.h2`
  margin: 0 0 0.45rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.2rem, 2.8vh, 1.65rem);
  font-weight: 700;
  line-height: 1.12;
  color: ${SAFARI.jungle};
`;

const SignupBody = styled.p`
  margin: 0 0 clamp(0.75rem, 1.5vh, 1rem);
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.78rem, 1.6vh, 0.88rem);
  line-height: 1.5;
  color: rgba(28, 28, 30, 0.62);
`;

const getTimeRemaining = () => {
  const now = Date.now();
  const target = GO_LIVE_DATE.getTime();
  const diff = Math.max(0, target - now);

  return {
    total: diff,
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
};

const SummerSafariLanding = () => {
  const [timeLeft, setTimeLeft] = useState(getTimeRemaining);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setTimeLeft(getTimeRemaining());
    }, 1000);

    return () => window.clearInterval(timer);
  }, []);

  const isLive = timeLeft.total === 0;

  return (
    <>
      <Helmet>
        <title>Belvoir Summer Safari — Family Day Out | Belvoir Castle, Leicestershire</title>
        <meta
          name="description"
          content="Follow the clues. Find the animals. Meet them for real. Belvoir Summer Safari is an estate-wide adventure trail for families at Belvoir Castle. Animal Park included. 13 July – 1 September 2026."
        />
        <meta
          name="keywords"
          content="Belvoir Summer Safari, family day out Leicestershire, things to do with kids East Midlands, Belvoir Castle summer 2026, animal park Leicestershire, family trail Grantham, children's adventure trail, Belvoir Castle tickets"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={getCanonicalUrl('belvoir-summer-safari')} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:title" content="Belvoir Summer Safari — Family Day Out at Belvoir Castle" />
        <meta
          property="og:description"
          content="Follow the clues. Find the animals. Meet them for real. Estate-wide adventure trail for families. Animal Park included with every ticket. 13 July – 1 September 2026."
        />
        <meta property="og:url" content={getCanonicalUrl('belvoir-summer-safari')} />
        <meta property="og:image" content="https://www.belvoircastle.com/wp/wp-content/uploads/2026/06/safari-header.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="A child on a rope swing at Belvoir Castle — Belvoir Summer Safari 2026" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@BelvoirCastle" />
        <meta name="twitter:title" content="Belvoir Summer Safari — Family Day Out at Belvoir Castle" />
        <meta
          name="twitter:description"
          content="Follow the clues. Find the animals. Meet them for real. Adventure playground trail — Animal Park included. 13 July – 1 September 2026."
        />
        <meta name="twitter:image" content="https://www.belvoircastle.com/wp/wp-content/uploads/2026/06/safari-header.png" />
      </Helmet>

      <Page>
        <Hero aria-label="Belvoir Summer Safari coming soon">
          <HeroInner>
            <CopyColumn>
              <Kicker>Summer 2026 · Coming Soon</Kicker>
              <Title>
                Belvoir <em>Summer Safari</em>
              </Title>
              <Strapline>
                &ldquo;Follow the clues. Find the animals. Meet them for real.&rdquo;
              </Strapline>

              <CountdownSection>
                {isLive ? (
                  <LiveMessage>The adventure begins — we&apos;re live!</LiveMessage>
                ) : (
                  <>
                    <CountdownLabel>Going live 19 June 2026</CountdownLabel>
                    <CountdownGrid aria-live="polite" aria-label="Countdown to launch">
                      <CountdownUnit>
                        <CountdownNumber>{timeLeft.days}</CountdownNumber>
                        <CountdownName>Days</CountdownName>
                      </CountdownUnit>
                      <CountdownUnit>
                        <CountdownNumber>{String(timeLeft.hours).padStart(2, '0')}</CountdownNumber>
                        <CountdownName>Hours</CountdownName>
                      </CountdownUnit>
                      <CountdownUnit>
                        <CountdownNumber>{String(timeLeft.minutes).padStart(2, '0')}</CountdownNumber>
                        <CountdownName>Minutes</CountdownName>
                      </CountdownUnit>
                      <CountdownUnit>
                        <CountdownNumber>{String(timeLeft.seconds).padStart(2, '0')}</CountdownNumber>
                        <CountdownName>Seconds</CountdownName>
                      </CountdownUnit>
                    </CountdownGrid>
                  </>
                )}
              </CountdownSection>
            </CopyColumn>

            <SignupCard>
              <SignupKicker>Early Access</SignupKicker>
              <SignupTitle>Something Wild Is Coming</SignupTitle>
              <SignupBody>
                Be the first to hear when Belvoir Summer Safari goes live.
              </SignupBody>
              <NewsletterSignUp variant="compact" tag="Summer School Holidays 2026" />
            </SignupCard>
          </HeroInner>
        </Hero>
      </Page>
    </>
  );
};

export default SummerSafariLanding;
